import React from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import Badge from "@react/shared/ui/Badge"
import { t, fmtHours, fmtLongDay } from "../helpers/format"
import { addDays } from "../helpers/dates"
import { STATUS_TONES } from "../helpers/states"
import PersonAvatar from "../components/PersonAvatar"

const StatusBadge = ({ day }) => {
  if (day.closed) {
    return (
      <Badge tone="slate" size="sm" className="gap-1">
        <FontAwesomeIcon icon="lock" className="text-[9px]" />
        {t("states.closed")}
      </Badge>
    )
  }
  return <Badge tone={STATUS_TONES[day.status] || "slate"} size="sm">{I18n.t(`project_hours.statuses.${day.status}`)}</Badge>
}

const DrawerHeader = ({ target, day, approved, onClose, onMove, settings }) => {
  const total   = day?.total_hours || 0
  const planned = day?.planned_hours || 0

  return (
    <header className="relative px-6 pt-5 pb-4 border-b border-slate-200">
      <div className="flex items-start gap-3">
        <PersonAvatar user={{ ...target.user, ...(day?.user || {}) }} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-[16px] font-semibold text-slate-900 truncate m-0">{target.user.name}</h2>
            {day && <StatusBadge day={day} />}
          </div>
          <div className="flex items-center gap-1 mt-0.5 -ml-1.5">
            <IconButton size="xs" onClick={() => onMove(addDays(target.date, -1))} title={t("drawer.previous_day")} aria-label={t("drawer.previous_day")}>
              <FontAwesomeIcon icon="chevron-left" />
            </IconButton>
            <span className="text-[12.5px] text-slate-600 first-letter:uppercase">{fmtLongDay(target.date)}</span>
            <IconButton size="xs" onClick={() => onMove(addDays(target.date, 1))} title={t("drawer.next_day")} aria-label={t("drawer.next_day")}>
              <FontAwesomeIcon icon="chevron-right" />
            </IconButton>
          </div>
        </div>
        <IconButton size="md" onClick={onClose} title={t("actions.close")} aria-label={t("actions.close")}>
          <FontAwesomeIcon icon="xmark" />
        </IconButton>
      </div>

      <div className="mt-4 flex items-end gap-5">
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{t("drawer.total")}</div>
          <div className={clsx("text-[28px] font-bold tabular-nums leading-[1.25]", day?.over_max ? "text-rose-600" : "text-slate-900")}>
            {day ? fmtHours(total) : <span className="hb-shimmer inline-block h-[24px] w-[70px] rounded align-middle" />}
          </div>
        </div>
        {day && planned > 0 && (
          <div className="pb-1.5">
            <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{t("drawer.planned")}</div>
            <div className="text-[15px] font-semibold tabular-nums text-slate-500">{fmtHours(planned)}</div>
          </div>
        )}
        {day && (day.absences || []).length > 0 && (
          <div className="pb-1.5 flex flex-wrap gap-1">
            {day.absences.map(absence => (
              <Badge key={absence.id} tone="violet" size="sm" className="gap-1">
                <FontAwesomeIcon icon="umbrella-beach" className="text-[9px]" />
                {absence.label || t("cell.absent")}
              </Badge>
            ))}
          </div>
        )}
        {day?.running && (
          <div className="pb-2 ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-brand-600">
            <span className="relative inline-flex w-[8px] h-[8px]">
              <span className="absolute inline-flex w-full h-full rounded-full bg-brand-400 opacity-75 animate-ping" />
              <span className="relative inline-flex w-[8px] h-[8px] rounded-full bg-brand-500" />
            </span>
            {t("legend.running")}
          </div>
        )}
      </div>

      {day?.over_max && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-[12px] text-rose-700">
          <span className="inline-grid place-items-center w-[16px] h-[16px] rounded-full bg-rose-600 text-white text-[10px] font-bold">!</span>
          {t("drawer.over_max", { hours: fmtHours(settings?.daily_max_hours) })}
        </div>
      )}

      {approved && (
        <div className="pointer-events-none absolute inset-0 grid place-items-center bg-white/70 animate-[hb-fade-out_1.4s_ease-out_forwards]">
          <span className="inline-grid place-items-center w-[64px] h-[64px] rounded-full bg-emerald-500 text-white text-[28px] animate-[hb-pop_500ms_cubic-bezier(.2,.9,.3,1.3)]">
            <FontAwesomeIcon icon="check" />
          </span>
        </div>
      )}
    </header>
  )
}

export default DrawerHeader
